import {createSelector} from '@reduxjs/toolkit'
import { 
    fullName, 
    combineAddress, 
    humanReadableDate, 
    decideLegalAndOperatingNames,
    decideRegistryId,
} from '../../app/utils'
import * as CONST from "../../app/constants"
import { currentAgentApplicationSelector } from './collectorApplicationSelector'

import { applicationTypeFromLicenceSubType, isAdministratorTypeApplication, isCollectionTypeApplication, isLenderTypeApplication, isMortgageTypeApplication } from '../slice/applicationsUtils'
import { collectionsReviewDataDetails } from './BLTReviewData/collectionsReviewData'
import { mortgageReviewDataDetails } from './BLTReviewData/mortgageReviewData'
import { lenderReviewDataDetails } from './BLTReviewData/lenderReviewData'
import { administratorReviewDataDetails } from './BLTReviewData/administratorReviewData'
import { parseCancelLicenceInformationMap } from './BLTReviewData/bltReviewMortgageApplication'

const contactDetails = (contactObj, separator = ", ") => {
    if(!contactObj) return ""
    const {phoneNumber, emailAddress} = contactObj
    const name = fullName(contactObj)
    const retArray = []
    if(name) {
        retArray.push(name)
    }
    if(phoneNumber) {
        retArray.push(phoneNumber)
    }
    if(emailAddress){
        retArray.push(emailAddress)
    }

    return retArray.join(separator)
}

export function parseApplicantInformation (applicationItem) { 
    let applicantInformation = []

    const {
        registryNumber,
        serviceCivicAddress,
        financialRecordsLocation,
        mainBusinessContact,
        complianceOfficer,
        responsibleAgent,
        companyRjscDetails
    } = applicationItem

    if(companyRjscDetails?.businessData?.relatedTo) { 
        applicantInformation.push(
            {
                "Registry ID" : companyRjscDetails.businessData.relatedTo,
                review : "complete",
            }
        )
    }
    else { 
        applicantInformation.push(
            {
                "Registry ID" : registryNumber ? registryNumber : "",
                review : "complete",
            }
        )
    }
    

    if(companyRjscDetails?.businessData?.businessOwner) {
        applicantInformation.push(
            {
                "Legal name" : companyRjscDetails.businessData.businessOwner,
                review : "complete",
            }
        )  
    } else { 
        applicantInformation.push(
            {
                "Legal name" : applicationItem.businessName ?? "",
                review : "complete",
            }
        )
    }

    applicantInformation.push(
        {
            "Business name" : applicationItem.businessName ?? "",
            review : "complete",
        }
    )

    applicantInformation.push(
        {
            "Trademark" : applicationItem.companyTrademark ?? "",
            review : "",
        }
    )

    let businessAddress = ""
    if(companyRjscDetails 
        && companyRjscDetails.businessContact) {

        if(companyRjscDetails.businessContact.homeJurisdictionAddress)
            businessAddress = combineAddress(companyRjscDetails.businessContact.homeJurisdictionAddress)
        else if(companyRjscDetails.businessContact.civicAddress)
            businessAddress = combineAddress(companyRjscDetails.businessContact.civicAddress)
    }
    
    applicantInformation.push(
        {
            "Business Address" : businessAddress, 
            review : "complete",
        }
    )

    applicantInformation.push(
        {
            "Address for service" : combineAddress(serviceCivicAddress),
            review : ""
        }
    )
    
    applicantInformation.push(
        {
            "Financial records location" : combineAddress(financialRecordsLocation),
            review : ""
        }
    )

    let officialsWithRjsc = []
    if(companyRjscDetails 
        && companyRjscDetails.businessData 
        && companyRjscDetails.businessData.peopleList
        && Array.isArray(companyRjscDetails.businessData.peopleList)) {
            companyRjscDetails.businessData.peopleList.forEach(p => {
                officialsWithRjsc.push(fullName(p) + ", " + p.position)
            })
    }

    applicantInformation.push(
        {
            "Officials with RJSC" : officialsWithRjsc.join("\n"),
            review : "complete",
        }
    )

    let emailAddress = ""
    if(mainBusinessContact 
        && mainBusinessContact.emailAddress) {
            emailAddress = mainBusinessContact.emailAddress
    }

    applicantInformation.push(
        {
            "Email address" : emailAddress,
            review : ""
        }
    )

    applicantInformation.push(
        {
            "Compliance officer" : contactDetails(complianceOfficer, "\n"),
            review : ""
        }
    )

    applicantInformation.push(
        {
            "Responsible agent" : contactDetails(responsibleAgent, "\n"),
            review : ""
        }
    )

    applicantInformation.push(
        {
            "Business contact" : contactDetails(mainBusinessContact,"\n"),
            review : ""
        }
    )



    return applicantInformation
}

export function parseCriminalHistory(applicationItem) {

    const {criminalHistoryData} = applicationItem
    let historyData = []

    if(criminalHistoryData 
        && Array.isArray(criminalHistoryData)) {
            
            criminalHistoryData.forEach((history) => { 
                const {
                    people, 
                    dateOfOffence,
                    description
                } = history

                let historyObj = {}

                let name = ""
                // criminal history for collector will not have "people" so name need not
                // appear in the review object
                if(people) { 
                    name = fullName(people)
                    Object.assign(historyObj, {"Name" : name})
                }

                historyData.push(Object.assign(historyObj, {
                    "Date of offence" : humanReadableDate(dateOfOffence),
                    "Offence Description" : description,
                    review : ""
                }))
            })
    }

    if(historyData.length === 0) {
        historyData.push({review : null})
    }

    return historyData
}

export function parseLicenceSuspension(applicationItem) {
    // collection and debt management agency applicationItem has licenceSuspendedData name
    // collector and debt management agent applicationItem has licenceSuspensionData name
    let licenceSuspendedData = applicationItem.licenceSuspendedData

    let suspensionData = []

    if(licenceSuspendedData && Array.isArray(licenceSuspendedData)) {
        licenceSuspendedData.forEach((suspension) => { 
            const {
                businessLicenceType,
                province,
                suspensionDate,
                reason
            } = suspension

            suspensionData.push({
                "Licence type" : businessLicenceType ? businessLicenceType : "",
                "Province" : province ? province : "",
                "Suspension date" : humanReadableDate(suspensionDate),
                "Reason" : reason ? reason : "",
                review : null
            })
        })
    }

    if(suspensionData.length === 0) {
        suspensionData.push({review : null})
    }

    return suspensionData
}

export function parseWorkHistory(applicationItem) { 
    const {workHistoryData} = applicationItem

    let history = []

    if(workHistoryData && Array.isArray(workHistoryData)) { 
        workHistoryData.forEach((_history) => {
            const {
                people,
                description
            } = _history

            history.push({
                "Name" : fullName(people),
                "Work history" : description,
                review : null
            })
        })
    }

    if(history.length === 0) { 
        history.push({review : null})
    }

    return history
}

export function parseSuretyBondRenewalData(documents) { 
    let documentsData = []

    documents?.forEach((d) => {
        if(d.documentType === CONST.DOCUMENT_TYPE_SURETY_BONDS && !d.isUpdated) { 
            documentsData.push({
                ...d,
                type : CONST.DOCUMENT_TYPE_SURETY_BONDS,
                review : null
            })
        }
    })

    if(documentsData.length === 0) { 
        documentsData.push({
            type : CONST.DOCUMENT_TYPE_SURETY_BONDS,
            review : null
        })
    }

    return documentsData
}

export function parseDocumentsData(documents) { 
    let documentsData = []

    documents?.forEach((d) => {
        if(d.documentType === CONST.DOCUMENT_TYPE_FORMS_AGREEMENTS && !d.isUpdated) { 
            documentsData.push({
                ...d,
                type : CONST.DOCUMENT_TYPE_FORMS_AGREEMENTS,
                review : null
            })
        } else if(d.documentType === CONST.DOCUMENT_TYPE_NOTICES_LETTERS && !d.isUpdated) { 
            documentsData.push({
                ...d,
                type : CONST.DOCUMENT_TYPE_NOTICES_LETTERS,
                review : null
            })
        }
    })

    if(documentsData.length === 0) { 
        documentsData.push({
            type : CONST.DOCUMENT_TYPE_FORMS_AGREEMENTS,
            review : null
        })

        documentsData.push({
            type : CONST.DOCUMENT_TYPE_NOTICES_LETTERS,
            review : null
        })
    }

    return documentsData
}

export function parseSuretyBondData(applicationItem) { 
    const {suretyBondReceivedDate} = applicationItem

    return [{
        "Received surety bond document?" : "",
        "review" : ""
    }, {
        "Date received surety bond document" : suretyBondReceivedDate ?? 0,
        "review" : suretyBondReceivedDate ? "complete" : null
    }]
}

function parseAgentInformation(agentApplication) { 
    let applicantInformation = []

    const {personData} = agentApplication
    const {phoneNumber} = personData

    applicantInformation.push(
        {
            "Name" : fullName(personData),
            review : ""
        }
    )

    applicantInformation.push(
        {
            "Email" : personData?.email,
            review : ""
        }
    )

    applicantInformation.push(
        {
            "Address" : combineAddress(personData?.addressData),
            review : ""
        }
    )

    applicantInformation.push(
        {
            "Phone number" : phoneNumber,
            review : ""
        }
    )

    return applicantInformation
}

export const isReviewDataNeeded = (applicationItem) => {
    return (!applicationItem.reviewDataCollectionDebt 
        || (Object.hasOwnProperty.call(applicationItem.reviewDataCollectionDebt,"applicantInformationMap")
            && Array.isArray(applicationItem.reviewDataCollectionDebt.applicantInformationMap)
            && applicationItem.reviewDataCollectionDebt.applicantInformationMap.length === 0)) 
}

// export const isNewDocumentsUploadedToReview = (applicationItem) => {
//     if(applicationItem.reviewDataCollectionDebt) {

//     }
// }

export const parseApplicationInformationForRenewal = (applicationItem, archiveApplicationItem) => {
    const applicantInformationFromData = (appData) => {
        const {legalName, operatingName} = decideLegalAndOperatingNames(appData?.companyRjscDetails)

        return {
            registryId : decideRegistryId(appData.companyRjscDetails),
            legalName : legalName,
            compnayTrademark : appData.companyTrademark,
            operatingName : operatingName,
            businessAddress : combineAddress(appData.companyRjscDetails?.businessContact?.homeJurisdictionAddress ?? appData.companyRjscDetails?.businessContact?.civicAddress),
            serviceAddress : combineAddress(appData.serviceCivicAddress),
            financialAddress : combineAddress(appData.financialRecordsLocation),
            rjscOfficials : appData.companyRjscDetails?.businessData?.peopleList?.map(p => fullName(p)).join("\n"),
            emailAddress : appData.mainBusinessContact?.emailAddress ?? "",
            complianceOfficer : contactDetails(appData.complianceOfficer, "\n"),
            mainBusinessContact : contactDetails(appData.mainBusinessContact, "\n"),
            responsibleAgent : contactDetails(appData.responsibleAgent, "\n")
        }
    }

    const mergeKeys = (obj1, obj2) => {
        const obj1Keys = Object.keys(obj1)
        const obj2Keys = Object.keys(obj2)
        
        const mergedKeys = []

        for (let index = 0; index < Math.max(obj1Keys.length, obj2Keys.length); index++) {

            if (index < obj1Keys.length && (mergedKeys.indexOf(obj1Keys[index]) === -1)) { 
                mergedKeys.push(obj1Keys[index])
            }
            
            if (index < obj2Keys.length && (mergedKeys.indexOf(obj2Keys[index]) === -1)) { 
                mergedKeys.push(obj2Keys[index])
            }
        }

        return mergedKeys
    }

    const changeFromObj = archiveApplicationItem ? applicantInformationFromData(archiveApplicationItem) : {}
    const changeToObj = applicantInformationFromData(applicationItem)
    let applicantInformation = []
    const parameters = mergeKeys(changeFromObj, changeToObj)

    parameters.forEach(key => {
        let fromElement = ""
        let toElement = ""

        if (Object.hasOwnProperty.call(changeFromObj, key)) {
            fromElement = changeFromObj[key];
        }

        if(Object.hasOwnProperty.call(changeToObj, key)) { 
            toElement = changeToObj[key]
        }

        applicantInformation.push({
            parameterName : key,
            changeFrom : fromElement,
            changeTo : toElement,
            review : (fromElement === toElement) ? "complete" : null,
        })
    })

    if (applicantInformation.every(obj => obj.review === "complete")) {
        applicantInformation[0].review = null
        applicantInformation[0].forceSelection = true
    }

    return applicantInformation
}

/*
export const parseRenewedAgentApplicationForRenewal = (agencyApplication, agentApplications) => {
    const renewedAgents = agentApplications?.filter(app => (
        isRenewalTypeApplication(app) && 
        (app.applicationStatus === CONST.APPLICATION_STATUS_UNREAD ||
        app.applicationStatus === CONST.APPLICATION_STATUS_OPENED ||
        app.applicationStatus === CONST.APPLICATION_STATUS_UNDER_REVIEW)
    ))

    let individualApplicationTitle = "Applications"
    if(agencyApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_RENEWAL) {
        individualApplicationTitle = "Collectors"
    } else if(agencyApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_RENEWAL) {
        individualApplicationTitle = "Debt Management Agents"
    }

    let retObj = {}
    Object.defineProperty(retObj, individualApplicationTitle, {
        value: renewedAgents.map(app => app.contactName).join("\n"), "review" : "",
        writable : true, 
        enumerable : true
    })
    
    return [retObj]
}
*/

export function bltAgentReviewData(applicationItem, application) {
    if(isReviewDataNeeded(applicationItem)) 
    {
        let applicantInformationMap = [{}], criminalHistoryMap = [{}], licenceSuspensionDataMap = [{}];

        if(application && application.applicationStatus === CONST.APPLICATION_STATUS_WITHDRAWN_BY_CLIENT) {
            applicantInformationMap = parseCancelLicenceInformationMap(applicationItem, application)
        }
        else {
            applicantInformationMap = parseAgentInformation(applicationItem)
            criminalHistoryMap = parseCriminalHistory(applicationItem)
            licenceSuspensionDataMap = parseLicenceSuspension(applicationItem)
        }
        
        const decisionMap = [{
            adminDecision : "",
            review : ""
        }]
        const internalNotesMap = [{
            internalNotes : applicationItem.internalNotes,
            review : applicationItem.internalNotes?.length > 0 ? "complete" : "" 
        }]
    
        return {
            applicantInformationMap,
            criminalHistoryMap,
            licenceSuspensionDataMap,
            internalNotesMap,
            decisionMap
        } 
    }

    return applicationItem.reviewDataCollectionDebt
}

const bltReviewSelector = createSelector(
    [state => state.bltComponents,
    state => state.primaryAgencyApplication,
    currentAgentApplicationSelector,
    state => state.archiveApplications.applications,
    state => state.additionalApplications,
    state => state.userAuth],
    (
        {bltReview}, 
        primaryAgencyApplication, 
        currentAgentApplication, 
        archiveApplications, 
        additionalApplications,
        {userData}
    ) => {

        const {queueItem} = bltReview
        let reviewDataCollectionDebt, sectionsOrder, internalNotes, businessLicenceData = null
        const archiveApplication = archiveApplications.filter(app => app.licenceNumber === queueItem?.licenceNumber)
        const branchArchiveApplications = archiveApplications.filter(app => (
                                                    app.licencingSubType === CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION
                                                    || app.licencingSubType === CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_RENEWAL))

        const queueApplicationType = applicationTypeFromLicenceSubType(queueItem?.licencingSubType);

        if(isCollectionTypeApplication(queueApplicationType)) {
            const collectionReviewData = collectionsReviewDataDetails(queueItem, primaryAgencyApplication, additionalApplications, currentAgentApplication);
            businessLicenceData = collectionReviewData.businessLicenceData;
            sectionsOrder = collectionReviewData.sectionsByOrder;
        } else if(isMortgageTypeApplication(queueApplicationType)) {
            const mortgageReviewData = mortgageReviewDataDetails(queueItem, primaryAgencyApplication, currentAgentApplication)
            businessLicenceData = mortgageReviewData.businessLicenceData;
            sectionsOrder = mortgageReviewData.sectionsByOrder;
        } else if(isLenderTypeApplication(queueApplicationType)) {
            const lenderReviewData = lenderReviewDataDetails(queueItem,primaryAgencyApplication);
            businessLicenceData = lenderReviewData.businessLicenceData
            sectionsOrder = lenderReviewData.sectionsByOrder
        } else if(isAdministratorTypeApplication(queueApplicationType)) {
            const administratorReviewData = administratorReviewDataDetails(queueItem, primaryAgencyApplication);
            businessLicenceData = administratorReviewData.businessLicenceData
            sectionsOrder = administratorReviewData.sectionsByOrder
        }

        reviewDataCollectionDebt = businessLicenceData?.reviewDataCollectionDebt
        internalNotes = businessLicenceData?.internalNotes

        // need to add internalNotesMap every time.
        if(reviewDataCollectionDebt) {
            reviewDataCollectionDebt = Object.assign({}, reviewDataCollectionDebt, {
                internalNotesMap : [{
                    internalNotes,
                    review : internalNotes?.length > 0 ? "complete" : ""
                }]
            })
        }

        return {
            queueItem,
            reviewDataCollectionDebt,
            sectionsOrder,
            userData,
            archiveApplication,
            branchArchiveApplications
        }
    }
)

export const bltReviewSelect = state => ({
    bltReview : bltReviewSelector(state)
})

export const btlReviewLicenceWithdrawSelect = createSelector(
    [
        state => state.primaryAgencyApplication,
        currentAgentApplicationSelector
    ],
    ({application}, currentAgentApplication) => {
        return {
            primaryAgencyApplication : application,
            currentAgentApplication
        }
    }
)